import { FC, useRef, useMemo } from 'react';
import { Icon, Button, useToast } from '@aidkitorg/component-library';
import { copyToClipboard } from '../../../../../../Util';
import { useLocalizedStrings } from '../../../../../../Localization';
import { ImageAttachmentProps } from '../shared/types';
import { useImageControls } from '../shared/hooks';

// Component to render an image attachment with controls
export const ImageAttachment: FC<ImageAttachmentProps> = ({ src, alt, attachmentType }) => {
  const L = useLocalizedStrings();
  const ref = useRef<HTMLImageElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const toast = useToast();

  const {
    rotation,
    scale,
    position,
    isDragging,
    hasError,
    handleImageError,
    createHandler,
    rotate,
    zoomIn,
    zoomOut,
    handleMouseDown,
    handleMouseMove,
    handleMouseUp,
    handleMouseLeave,
  } = useImageControls(ref);

  const copyLink = useMemo(() => createHandler(() => {
    copyToClipboard(src);
    toast.toast({ variant: 'success', title: L.questions?.attachment?.copied_link || L.applicant.comms.download_attachment });
  }), [src, createHandler, toast, L]);

  if (hasError) {
    return (
      <div className="text-sm text-gray-600 italic">
        [{L.applicant.comms.attachment_failed_to_load}{attachmentType ? `. ${L.applicant.comms.file_type}: ${attachmentType}` : ''}]
      </div>
    );
  }

  return (
    <div className="p-1">
      <div
        ref={containerRef}
        className="max-w-[400px] max-h-[300px] overflow-hidden relative"
        style={{
          cursor: scale > 1 ? (isDragging ? 'grabbing' : 'grab') : 'default',
        }}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseLeave}
      >
        <img
          ref={ref}
          alt={alt}
          style={{
            maxWidth: '400px',
            maxHeight: '300px',
            transform: `translate(${position.x}px, ${position.y}px) scale(${scale}) rotate(${rotation}deg)`,
            transformOrigin: 'center',
            transition: isDragging ? 'none' : 'transform 0.2s ease-out',
            userSelect: 'none',
          }}
          src={src}
          className="rounded-lg"
          draggable={false}
          onError={handleImageError}
        />
      </div>
      <div className="flex gap-2 mt-2">
        <Button
          variant="secondary"
          size="sm"
          onClick={rotate}
          icon={<Icon name="ArrowPathIcon" />}
        >
          {L.questions?.attachment?.rotate_image || 'Rotate'}
        </Button>
        <Button
          variant="secondary"
          size="sm"
          onClick={zoomIn}
          icon={<Icon name="ArrowsPointingOutIcon" />}
        >
          {L.questions?.attachment?.zoom_in || 'Zoom In'}
        </Button>
        <Button
          variant="secondary"
          size="sm"
          onClick={zoomOut}
          icon={<Icon name="ArrowsPointingInIcon" />}
        >
          {L.questions?.attachment?.zoom_out || 'Zoom Out'}
        </Button>
        <Button
          variant="secondary"
          size="sm"
          onClick={copyLink}
          icon={<Icon name="LinkIcon" />}
        >
          {L.questions?.attachment?.copy_link || 'Copy Link'}
        </Button>
      </div>
    </div>
  );
};
